// ── 유틸리티 함수, 순수 함수, 소형 UI 컴포넌트 ──────────────────────────────
import React from 'react';
import { todayKST } from '../../../utils/date';

/** 모바일 분기점 */
export const MOBILE_BREAKPOINT = 768;

/**
 * contract_items 배열 → 폼 상태로 변환
 * 반환: { selectedProducts, productCounts, productValidities }
 */
function contractItemsToFormState(items) {
  const filtered = (items || []).filter((i) => !i.isService);
  const selectedProducts  = filtered.map((i) => i.ticketTypeName || '');
  const productCounts     = {};
  const productValidities = {};
  filtered.forEach((i) => {
    const name = i.ticketTypeName || '';
    if (i.quantity    != null) productCounts[name]     = String(i.quantity);
    if (i.validityMonths != null) productValidities[name] = String(i.validityMonths);
  });
  return { selectedProducts, productCounts, productValidities };
}

/**
 * contract_items 배열(서비스 항목) → 폼 상태로 변환
 */
function serviceItemsToFormState(items) {
  const filtered = (items || []).filter((i) => i.isService);
  const selectedProducts  = filtered.map((i) => i.ticketTypeName || '');
  const productCounts     = {};
  const productValidities = {};
  filtered.forEach((i) => {
    const name = i.ticketTypeName || '';
    if (i.quantity    != null) productCounts[name]     = String(i.quantity);
    if (i.validityMonths != null) productValidities[name] = String(i.validityMonths);
  });
  return { selectedProducts, productCounts, productValidities };
}

/** KST 오늘 날짜 YYYY-MM-DD */
export function getKstTodayYmd() {
  // todayKST: utils/date.js 사용 (new Date 직접 사용 금지)
  return todayKST();
}

/** 선택된 상품의 수량 합산 — 선택 없으면 null */
export function sumCounts(selectedProducts, productCounts) {
  if (!selectedProducts || selectedProducts.length === 0) return null;
  const total = selectedProducts.reduce((sum, name) => {
    const cnt = Number((productCounts || {})[name] || 0);
    return sum + (Number.isFinite(cnt) ? cnt : 0);
  }, 0);
  return total > 0 ? total : null;
}

/** 필수 항목 표시 레이블 (빨간 별표 포함) */
export function Req({ label }) {
  return <>{label} <span style={{ color: '#ef4444', fontWeight: 700 }}>*</span></>;
}

/** 폼 필드 래퍼 — 레이블 + 입력 컴포넌트 그리드 */
export function FormField({ label, children }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{label}</div>
      {children}
    </div>
  );
}

/**
 * ContractModal 의 initForm 로직을 순수 함수로 추출
 * 모달 props(isNew, isReorder, row, externalCategories, formDesignCourier)를 받아
 * 초기 폼 상태 객체를 반환
 */
export function buildInitForm({ isNew, isReorder, row, externalCategories, formDesignCourier }) {
  // contract_items에 서비스 항목이 없으면 externalCategories 기본상품으로 초기화
  function resolveDefaultServiceProducts(contractItems, existingSp) {
    if (existingSp.length > 0) return existingSp;
    // 첫 번째 주계약 상품명으로 카테고리 기본 서비스 상품 탐색
    const firstNormal = (contractItems || []).find((i) => !i.isService);
    const match = (externalCategories || []).find((c) => c.name === (firstNormal?.ticketTypeName || ''));
    if (match?.default_product_name) return [match.default_product_name];
    return [];
  }

  // 재주문: 최신 계약 정보 전부 채우되 계약일자는 오늘 날짜로 초기화
  // registrationType: 'reorder' (재주문 버튼 클릭 시 기본 선택)
  if (isNew && isReorder && row) {
    const contractItems = Array.isArray(row?.contract_items) ? row.contract_items : [];
    const { selectedProducts: cp, productCounts: cc, productValidities: cv } = contractItemsToFormState(contractItems);
    const { selectedProducts: rawSp, productCounts: sc, productValidities: sv } = serviceItemsToFormState(contractItems);
    const sp = resolveDefaultServiceProducts(contractItems, rawSp);
    return {
      registrationType: 'reorder',  // 신규/재주문 토글 기본값
      company_name: row.company_name || '', phone: row.phone || '',
      mobile_phone: row.mobile_phone || '', address: row.address || '',
      user_name: row.user_name || '', sales_person_name: row.sales_person_name || '',
      contract_date:      getKstTodayYmd(),
      contractProducts:   cp, contractCounts: cc, contractValidities: cv,
      serviceProducts:    sp, serviceCounts:  sc, serviceValidities:  sv,
      promoItems: [], promoReceivedDate: getKstTodayYmd(), promoMemo: '',
      // 재주문: 이전 계약의 비고 인계, 배송정보는 새 송장으로 시작 (택배사만 기본값 유지)
      memo: row.memo || '',
      deliveryItems: [{ courier: formDesignCourier || '', tracking_number: '' }],
    };
  }
  // 신규 직접 등록: 계약일자 오늘 날짜 기본값
  // registrationType: 'new' (신규 계약 등록 버튼 클릭 시 기본 선택)
  if (isNew) {
    return {
      registrationType: 'new',  // 신규/재주문 토글 기본값
      company_name: '', phone: '', mobile_phone: '', address: '',
      user_name: '', sales_person_name: '',
      contract_date:      getKstTodayYmd(),
      contractProducts:   [], contractCounts: {}, contractValidities: {},
      serviceProducts:    [], serviceCounts:  {}, serviceValidities:  {},
      promoItems: [], promoReceivedDate: getKstTodayYmd(), promoMemo: '',
      courier: '', tracking_number: '', memo: '',
      // 배송 정보가 신규 등록에도 보이므로 빈 줄 하나로 시작한다 (2026-09-22).
      //   예전에는 이 창에 배송 칸이 없어서 아예 만들지 않았다.
      deliveryItems: [{ courier: formDesignCourier || '', tracking_number: '', is_return: false }],
    };
  }
  // 기존 레코드 수정 — contract_items 배열 기반
  // registrationType:
  //   - 미계약 행(계약 등록): 'new' 기본 선택 (신규 계약 등록 흐름)
  //   - 계약완료 행(수정): null (실수로 신규/재주문으로 바뀌는 것 방지)
  const contractItems = Array.isArray(row?.contract_items) ? row.contract_items : [];
  const { selectedProducts: cp, productCounts: cc, productValidities: cv } = contractItemsToFormState(contractItems);
  const { selectedProducts: rawSp, productCounts: sc, productValidities: sv } = serviceItemsToFormState(contractItems);
  const sp = resolveDefaultServiceProducts(contractItems, rawSp);
  return {
    registrationType: row?.is_reorder ? 'reorder' : 'new',  // 기존 is_reorder 기반으로 항상 선택 상태 유지
    company_name: row?.company_name || '', phone: row?.phone || '',
    mobile_phone: row?.mobile_phone || '', address: row?.address || '',
    user_name: row?.user_name || '', sales_person_name: row?.sales_person_name || '',
    // 수정 모드: 기존 계약일자 유지, 없으면 오늘 날짜 기본값 (미계약 행에서 계약 등록 시 대비)
    contract_date:   row?.contract_date || getKstTodayYmd(),
    contract_status: row?.contract_status || 'normal',
    contractProducts: cp, contractCounts: cc, contractValidities: cv,  // contract_items 주계약
    serviceProducts:  sp, serviceCounts:  sc, serviceValidities:  sv,  // contract_items 서비스
    promoItems: [], promoReceivedDate: getKstTodayYmd(), promoMemo: '',
    memo: row?.memo || '',
    // 다중 배송 정보: delivery_items 우선, 없으면 courier/tracking_number 단일 값으로 복원
    // is_return: 회수 여부 — true이면 정산서 택배비 ×2 적용
    deliveryItems: Array.isArray(row?.delivery_items) && row.delivery_items.length > 0
      ? row.delivery_items.map((i) => ({ courier: i.courier || '', tracking_number: i.tracking_number || '', is_return: Boolean(i.is_return), ...(i.is_pickup ? { is_pickup: true } : {}) }))
      : (row?.courier || row?.tracking_number)
        ? [{ courier: row.courier || '', tracking_number: row.tracking_number || '', is_return: false }]
        : [{ courier: formDesignCourier || '', tracking_number: '', is_return: false }],
  };
}

/**
 * ContractModal 필수 항목 검증
 * 반환: { [필드명]: true } (에러 있는 필드만 포함)
 */
export function validateContractForm(form) {
  const e = {};
  if (!form.registrationType)         e.registrationType  = true;  // 신규/재주문 필수 선택
  if (!form.company_name.trim())      e.company_name      = true;
  if (!form.phone.trim())             e.phone             = true;
  if (!form.mobile_phone.trim())      e.mobile_phone      = true;
  if (!form.address.trim())           e.address           = true;
  if (!form.sales_person_name)        e.sales_person_name = true;
  if (!form.contract_date)            e.contract_date     = true;
  // 계약상품은 필수 아님 — 미선택 시 모달에서 확인 팝업으로 처리
  // 장수도 필수가 아니다(2026-09-22 사용자 요청). 비우고 저장하면 **0장**으로 기록되고
  //   나중에 계약을 열어 채우면 된다. 0 은 상황판·정산 합계에 그대로 0 으로 들어가
  //   숫자가 부풀려지지 않는다(예전 서버 폴백은 빈 값을 1장으로 바꿔 놨었다).
  return e;
}
